import api from './utils/axios';

interface ExportFilters {
  start_date?: string;
  end_date?: string;
  chunk_size?: number;
  batch_size?: number;
  include_discussion?: boolean;
  identify_user?: boolean;
  tenant_id?: number;
}

export class ExportService {
  private readonly PRACTICES_PREFIX = 'agentos/practices';
  private readonly USER_ACCESS_PREFIX = 'agentos/user-access-analytics';
  private readonly ANALYTICS_PREFIX = 'agentos/analytics';

  private getFilenameFromResponse(response: any): string {
    const contentDisposition = response.headers['content-disposition'];
    console.log('contentDisposition', contentDisposition);
    if (contentDisposition) {
      // Extract filename from attachment; filename="example.csv"
      const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
      if (filenameMatch && filenameMatch[1]) {
        return filenameMatch[1];
      }
    }
    return '';
  }

  /**
   * Exports practices data as CSV
   * @param filters Optional filters for the export
   * @returns Promise containing the CSV data and filename
   */
  async exportPractices(filters?: ExportFilters) {
    try {
      const response = await api.get(`${this.PRACTICES_PREFIX}/export/`, {
        params: filters,
        responseType: 'blob',
      });
      const filename = this.getFilenameFromResponse(response);
      return {
        blob: response.data,
        filename,
      };
    } catch (error) {
      console.error('Error exporting practices:', error);
      throw error;
    }
  }

  /**
   * Exports user access analytics data as CSV
   * @param filters Optional filters for the export
   * @returns Promise containing the CSV data and filename
   */
  async exportUserAccessAnalytics(filters?: ExportFilters) {
    try {
      const response = await api.get(`${this.USER_ACCESS_PREFIX}/export/`, {
        params: filters,
        responseType: 'blob',
      });
      const filename = this.getFilenameFromResponse(response);
      return {
        blob: response.data,
        filename,
      };
    } catch (error) {
      console.error('Error exporting user access analytics:', error);
      throw error;
    }
  }

  /**
   * Generate user engagement analytics report (async, sent via email)
   * @param filters Optional date range filters
   * @returns Promise containing the response message
   */
  async generateUserEngagementReport(filters?: ExportFilters) {
    try {
      const response = await api.post(
        `${this.ANALYTICS_PREFIX}/user-engagement/`,
        null,
        { params: filters }
      );
      return response.data;
    } catch (error) {
      console.error('Error generating user engagement report:', error);
      throw error;
    }
  }

  /**
   * Generate agent analytics report (async, sent via email)
   * @param filters Optional date range filters
   * @returns Promise containing the response message
   */
  async generateAgentAnalyticsReport(filters?: ExportFilters) {
    try {
      const response = await api.post(
        `${this.ANALYTICS_PREFIX}/agent-analytics/`,
        null,
        { params: filters }
      );
      return response.data;
    } catch (error) {
      console.error('Error generating agent analytics report:', error);
      throw error;
    }
  }

  /**
   * Generate system access metrics report (async, sent via email)
   * @param filters Optional date range filters
   * @returns Promise containing the response message
   */
  async generateSystemAccessMetricsReport(filters?: ExportFilters) {
    try {
      const response = await api.post(
        `${this.ANALYTICS_PREFIX}/system-access-metrics/`,
        null,
        { params: filters }
      );
      return response.data;
    } catch (error) {
      console.error('Error generating system access metrics report:', error);
      throw error;
    }
  }
}

export default new ExportService();
